import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, useBlocker, Location } from 'react-router-dom';

interface NavigationWarningOptions {
  shouldWarn?: boolean;
  onConfirm?: () => void;
  message?: string;
}

interface BlockerCallbackParams {
  currentLocation: Location;
  nextLocation: Location;
}

export const useNavigationWarning = ({
  shouldWarn = false,
  onConfirm,
  message = 'You have unsaved changes that will be lost if you leave this page.'
}: NavigationWarningOptions = {}) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<string | null>(null);
  
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: BlockerCallbackParams) => {
        if (!shouldWarn) return false;
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return true;
      },
      [shouldWarn]
    )
  );

  // Show the dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === "blocked") {
      setPendingLocation(blocker.location.pathname);
      setShowDialog(true);
    }
  }, [blocker]);

  // Handle browser's back/forward buttons and tab/window closing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldWarn) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldWarn, message]);

  const handleNavigationAttempt = (to: string) => {
    if (shouldWarn) {
      setPendingLocation(to);
      setShowDialog(true);
      return false;
    }
    navigate(to);
    return true;
  };

  const handleConfirmNavigation = () => {
    setShowDialog(false);
    if (onConfirm) {
      onConfirm();
    }
    
    if (blocker.state === "blocked") {
      // If navigation was blocked by the router
      blocker.proceed();
    } else if (pendingLocation) {
      // If navigation was triggered manually
      navigate(pendingLocation);
    }
    setPendingLocation(null);
  };

  const handleCancelNavigation = () => {
    setShowDialog(false);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setPendingLocation(null);
  };

  return {
    showDialog,
    handleNavigationAttempt,
    handleConfirmNavigation,
    handleCancelNavigation
  };
};